<script setup>
import PencilSimpleIcon from '@iconify-vue/ph/pencil-simple';
import PlusIcon from '@iconify-vue/ph/plus';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import PillCell from '@js/components/tables/cells/PillCell.vue';
import StatusCell from '@js/components/tables/cells/StatusCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { tenant } = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'name',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Nom',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.name,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'discountType',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Type de réduction',
                }),
            cell: (props) =>
                h(PillCell, {
                    pillValue: props.row.original.discountType.label,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'discountValue',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Valeur',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.discountValue,
                    withDecimals: true,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'minimumCartAmount',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Panier min.',
                }),
            cell: (props) =>
                props.row.original.minimumCartAmount !== null
                    ? h(NumberCell, {
                          number: props.row.original.minimumCartAmount,
                          withDecimals: true,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'validityDays',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Validité (jours)',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.validityDays,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'codesCount',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Codes générés',
                }),
            enableSorting: false,
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.codesCount,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'isActive',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Est actif',
                }),
            cell: (props) =>
                h(StatusCell, {
                    status: props.row.original.isActive,
                }),
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () =>
                            h(ActionButton, {
                                size: 'small',
                                title: 'Modifier',
                                href: route(
                                    'front.projectManagement.modules.emailingAutomationPromoPriceRule',
                                    {
                                        tenant: tenant.id,
                                        promoPriceRule: props.row.original.id,
                                    },
                                ),
                                icon: PencilSimpleIcon,
                                square: true,
                            }),
                    },
                ),
        }),
    ],
    url: route(
        'front.projectManagement.modules.postEmailingAutomationPromoPriceRules',
        {
            tenant: tenant.id,
        },
    ),
    columnPinning: {
        right: ['actions'],
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :filters="filters"
        :available-filters="availableFilters"
        :set-filters="setFilters"
        :set-global-filter="setGlobalFilter"
        :loading="loading"
    >
        <template #actions>
            <ActionButton
                size="small"
                :href="
                    route(
                        'front.projectManagement.modules.emailingAutomationPromoPriceRule',
                        {
                            tenant: tenant.id,
                        },
                    )
                "
                :left-icon="PlusIcon"
            >
                Ajouter
            </ActionButton>
        </template>
    </DataTableLayout>
</template>
